<template>
    <vx-modal size="md" padding="standard" :visible="!!model && !token" @update:visible="model = null">
        <template v-slot:header> {{ model.id ? 'Update' : 'Create' }} API Token </template>

        <form @submit.prevent="submit()" id="token-form">
            <div>
                <vx-label>Token Name</vx-label>
                <vx-input
                    type="text"
                    size="lg"
                    name="name"
                    v-model="model.name"
                    :error="displayFirstError(validate.name.$errors)"
                />
            </div>
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="model = null"
                    tabindex="1"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    :loading="submitting"
                    form="token-form"
                    tabindex="0"
                    data-test="confirm-button"
                    type="submit"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Create' }} API Token
                </vx-button>
            </div>
        </template>
    </vx-modal>

    <vx-modal
        class="max-w-lg"
        padding="standard"
        :visible="!!token"
        @update:visible="
            token = null;
            model = null;
        "
    >
        <template v-slot:header> API Token Created </template>

        <template v-slot:subheader>
            Your API Token has been created. Store it somewhere safe. You won't be able to see the token again.
        </template>

        <vx-alert color="info" :closeable="false">
            Here is your new API Token.
            <strong>This is the only time the token will ever be displayed, so be sure not to lose it!</strong>
            You may revoke the token at any time from your API settings.
        </vx-alert>
        <vx-popover hover arrow placement="top">
            <div
                @click.prevent="copyToken()"
                data-test="copy-token"
                class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm mt-4"
            >
                {{ token }} <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
            </div>
            <template v-slot:content> Click to Copy </template>
        </vx-popover>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="
                        model = null;
                        token = null;
                    "
                    tabindex="1"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Close
                </vx-button>
                <vx-button @click.prevent="copyToken()" type="button" size="lg" block color="primary">
                    Copy Token <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { VxAlert, VxLabel, VxInput, VxModal, VxButton, VxPopover } from '@voxie/frontend-components';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import axios from '~/services/axios';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { useCopyText } from '~/composables/useCopyText';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

const model = defineModel({
    type: Object,
    required: false,
});

const submitting = ref(false);
const success = ref(false);
const token = ref(null);
const { $externalResults, catchError } = useFormRequestHandler();

const { copyText } = useCopyText();

const copyToken = () => {
    copyText(token.value);
};

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(80) },
    },
    model,
    { $externalResults }
);

watch(
    () => !!model.value,
    async () => {
        validate.value.$reset();
    }
);

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    success.value = false;
    submitting.value = true;

    try {
        if (model.value.id) {
            await axios.put(`/settings/api/token/${model.value.id}`, model.value);
            model.value = null;
        } else {
            const response = await axios.post(`/settings/api/token`, model.value);
            token.value = response.data.token;
        }

        Bus.$emit('updateTokens');

        validate.value.$reset();
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
